/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { jsx } from '@atlaskit/css';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { BLOCK_CONTROLS_RIGHT_SURFACE } from '@atlaskit/editor-common/block-controls/surface-keys';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { createSurfaceContext } from '@atlaskit/editor-ui-control-model/types';

import type { BlockControlsPlugin } from '../blockControlsPluginType';
import { getNodeTypeWithLevel } from '../pm-plugins/decorations-common';

import { BlockControlsRightSurface } from './block-controls-right-surface';
import {
	createBlockControlsSurfaceContext,
	createBlockControlsSurfaceContextForPosition,
} from './block-controls-surface-context';
import { getBlockControlsSurfaceTargets } from './block-controls-surface-targets';
import { getNodeContentElement } from './utils/get-node-content-element';
import {
	getAbsoluteSurfacePlacement,
	getSurfacePlacement,
	type SurfacePlacement,
} from './utils/get-surface-placement';

const EMPTY_SURFACE_POSITIONS: readonly number[] = [];
const EMPTY_SURFACE_COMPONENTS: [] = [];

type Props = {
	api: ExtractInjectionAPI<BlockControlsPlugin>;
	editorView: EditorView;
};

/**
 * Registry-backed right surface tree. Mirrors the left surface's single-tree, measured-placement
 * approach, but keeps its own components lookup, targets, placements and measurement lifecycle so
 * the right surface never depends on the left surface's render pass.
 */
export const BlockControlsRightSurfaces = ({
	api,
	editorView,
}: Props): React.JSX.Element | null => {
	const { activeNode, surfaceNodePositions } = useSharedPluginStateWithSelector(
		api,
		['blockControls'],
		(states) => ({
			activeNode: states.blockControlsState?.activeNode,
			surfaceNodePositions:
				states.blockControlsState?.surfaceNodePositions ?? EMPTY_SURFACE_POSITIONS,
		}),
	);
	const components =
		api.uiControlRegistry?.actions.getComponents(BLOCK_CONTROLS_RIGHT_SURFACE) ??
		EMPTY_SURFACE_COMPONENTS;
	const targets = useMemo(
		() => getBlockControlsSurfaceTargets(activeNode, surfaceNodePositions),
		[activeNode, surfaceNodePositions],
	);
	const surfaces = useMemo(
		() =>
			targets.flatMap((target) => {
				const context =
					target.source === 'stored'
						? createBlockControlsSurfaceContextForPosition(
								editorView.state,
								target.position,
								activeNode,
							)
						: createBlockControlsSurfaceContext(editorView, activeNode, target.source);
				if (!context) {
					return [];
				}
				const surfaceContext = createSurfaceContext(BLOCK_CONTROL_UI_CONTEXT, context);
				return [{ ...target, blockControlsContext: context, surfaceContext }];
			}),
		[activeNode, editorView, targets],
	);
	const [placements, setPlacements] = useState<ReadonlyMap<number, SurfacePlacement>>(new Map());
	const animationFrameRef = useRef<number | undefined>(undefined);

	useLayoutEffect(() => {
		if (surfaces.length === 0) {
			setPlacements(new Map());
			return;
		}

		const measurementTargets = surfaces.flatMap(({ blockControlsContext, position }) => {
			const targetNodeElement = editorView.nodeDOM(blockControlsContext.targetNode.pos);
			const targetElementRoot =
				targetNodeElement instanceof HTMLElement
					? targetNodeElement
					: targetNodeElement?.parentElement;
			if (!targetElementRoot) {
				return [];
			}

			return [
				{
					blockControlsContext,
					position,
					targetElement: getNodeContentElement(
						targetElementRoot,
						blockControlsContext.targetNode.type.name,
					),
					targetElementRoot,
				},
			];
		});
		const offsetParent = editorView.dom.offsetParent;
		const isDocumentOffsetParent = !offsetParent || offsetParent === getDocument()?.body;
		const scrollingOffsetParent =
			offsetParent instanceof HTMLElement && !isDocumentOffsetParent ? offsetParent : undefined;

		const updatePositions = () => {
			animationFrameRef.current = undefined;
			const offsetParentRect = offsetParent?.getBoundingClientRect();
			const offsetParentScrollTop = scrollingOffsetParent?.scrollTop ?? window.scrollY;
			const offsetParentScrollLeft = scrollingOffsetParent?.scrollLeft ?? window.scrollX;
			const nextPlacements = new Map<number, SurfacePlacement>();

			for (const {
				blockControlsContext,
				position,
				targetElement,
				targetElementRoot,
			} of measurementTargets) {
				if (!targetElementRoot.isConnected || !targetElement.isConnected) {
					continue;
				}
				const placement = getSurfacePlacement({
					layout: targetElementRoot.getAttribute('layout') ?? '',
					nodeRect: targetElement.getBoundingClientRect(),
					nodeType: blockControlsContext.targetNode.type.name,
					nodeTypeWithLevel: getNodeTypeWithLevel(blockControlsContext.targetNode.node),
					parentNodeType:
						blockControlsContext.targetNode.parentType === 'doc'
							? undefined
							: blockControlsContext.targetNode.parentType,
					side: 'right',
				});
				nextPlacements.set(
					position,
					getAbsoluteSurfacePlacement({
						offsetParentRect,
						offsetParentScrollLeft,
						offsetParentScrollTop,
						placement,
					}),
				);
			}
			setPlacements(nextPlacements);
		};
		const schedulePositionUpdate = () => {
			if (animationFrameRef.current === undefined) {
				animationFrameRef.current = requestAnimationFrame(updatePositions);
			}
		};

		updatePositions();
		const resizeObserver = new ResizeObserver(schedulePositionUpdate);
		const observedElements = new Set<Element>([editorView.dom]);
		for (const { targetElement } of measurementTargets) {
			observedElements.add(targetElement);
		}
		if (offsetParent instanceof HTMLElement) {
			observedElements.add(offsetParent);
		}
		observedElements.forEach((element) => resizeObserver.observe(element));

		const unbindWindowResize = bind(window, {
			type: 'resize',
			listener: schedulePositionUpdate,
		});
		const unbindWindowScroll = bind(window, {
			type: 'scroll',
			listener: schedulePositionUpdate,
			options: { capture: true, passive: true },
		});
		const visualViewport = window.visualViewport;
		const unbindVisualViewportResize = visualViewport
			? bind(visualViewport, { type: 'resize', listener: schedulePositionUpdate })
			: undefined;
		const unbindVisualViewportScroll = visualViewport
			? bind(visualViewport, { type: 'scroll', listener: schedulePositionUpdate })
			: undefined;
		const unbindTransitionEnd = bind(editorView.dom, {
			type: 'transitionend',
			listener: schedulePositionUpdate,
			options: { capture: true },
		});

		return () => {
			if (animationFrameRef.current !== undefined) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = undefined;
			}
			resizeObserver.disconnect();
			unbindWindowResize();
			unbindWindowScroll();
			unbindVisualViewportResize?.();
			unbindVisualViewportScroll?.();
			unbindTransitionEnd();
		};
	}, [editorView, surfaces]);

	return (
		<>
			{surfaces.map(({ position, source, surfaceContext }) => (
				<BlockControlsRightSurface
					api={api}
					components={components}
					forceVisibleOnMouseOut={Boolean(activeNode?.handleOptions?.isFocused)}
					key={position}
					placement={placements.get(position)}
					source={source}
					surfaceContext={surfaceContext}
				/>
			))}
		</>
	);
};
